// Sidebar.jsx
import React, { useState } from 'react';
import styles from './Sidebar.module.css';
import NavItem from './navItem/NavItem';
import { LocalStorageKeys } from '@/utils/localStorageUtils';
import { useLocation, useSearch } from 'wouter';
import useFeatureFlag from '@/hooks/useFeatureflag';
import { IconBoxemB } from '@/components/icons/IconBoxemB';
import { useUserContext } from '@/contexts/UserContext';
import { UserRole } from '@boxem/core-api-schema';

export const SmallTarget = { width: 1366, height: 768 };

export interface NavItemOption {
  id: string;
  label: string;
  onClick: () => void;
  href: string;
  disabled?: boolean;
  disabledTooltip?: string;
}

const Sidebar = () => {
  // const searchParams = new URLSearchParams(useSearch());
  // const launchAnalytics: string = searchParams.get("analytics");
  const { user } = useUserContext();
  const launchAnalytics = useFeatureFlag('analytics');
  // Sidebar expansion state
  const [isExpanded, setIsExpanded] = useState(!window.matchMedia(`(max-width: ${SmallTarget.width}px)`).matches && !window.localStorage.getItem(LocalStorageKeys.sidebarCollapsed));

  const [location, navigate] = useLocation();

  const [openDropdowns, setOpenDropdowns] = useState(() => {
    // Initialize all dropdowns as closed
    const initialState = {
      inventory: false,
      shipments: false,
      analytics: false,
    };
    
    // Check if location starts with any of these paths and open the corresponding dropdown
    if (location.startsWith('/inventory') || location.startsWith('/fbm/listings') || location.startsWith('/fba/listings')) {
      initialState.inventory = true;
    } else if (location.startsWith('/shipments') || location.startsWith('/fba/shipments')) {
      initialState.shipments = true;
    } else if (location.startsWith('/analytics')) {
      initialState.analytics = true;
    }
    
    return initialState;
  });
  
  const toggleSidebar = () => {
    if (!isExpanded) {
        window.localStorage.removeItem(LocalStorageKeys.sidebarCollapsed);
      } else {
        window.localStorage.setItem(LocalStorageKeys.sidebarCollapsed, "true");
      }
      setIsExpanded(!isExpanded)
  };

  const handleNavItemClick = (itemName: string, href: string, e: MouseEvent) => {

    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      window.open(href, '_blank');
      return;
    }

    const isAlreadyActive = location === href;
    navigate(href);
    
    // // Check if this nav item has a dropdown
    // if (itemName in openDropdowns) {
      // If already active, just toggle its dropdown
    if (isAlreadyActive) {
      toggleDropdown(itemName, !openDropdowns[itemName]);
    } else {
      // If not already active, open this dropdown and close others
      const updatedDropdowns = {};
      // First set all dropdowns to false
      Object.keys(openDropdowns).forEach(key => {
        updatedDropdowns[key] = false;
      });
      // Then set this dropdown to true
      updatedDropdowns[itemName] = true;
      setOpenDropdowns(updatedDropdowns as any);
    }
    // }
  };

  // Toggle dropdown open/closed
  const toggleDropdown = (navItem, isOpen) => {
    setOpenDropdowns({
      ...openDropdowns,
      [navItem]: isOpen
    });
  };

  // Handle dropdown option click
  const handleOptionClick = (href: string, e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      window.open(href, '_blank');
    } else {
      navigate(href);
    }
  };

  // Example dropdown options
  const inventoryOptions: NavItemOption[] = [
    { 
      id: 'list-fbm',
      label: 'List FBM', 
      onClick: () => console.log('Navigating to List FBM'),
      href: '/fbm/listings/create'
    },
    { 
      id: 'list-fba',
      label: 'List FBA', 
      onClick: () => console.log('Navigating to List FBA'),
      href: '/fba/listings/create'
    }
  ];

  const shipmentsOptions: NavItemOption[] = [
    {
        id: 'create-shipment',
        label: 'Create Shipment',
        onClick: () => console.log('Navigating to Create Shipment'),
        href: '/fba/shipments/create',
        disabled: !user.sellerId && user.role !== UserRole.ASSISTANT,
        disabledTooltip: !user.sellerId && user.role !== UserRole.ASSISTANT ? "Connect your Amazon Seller Account first!" : "",
    },
  ]

  const analyticsOptions: NavItemOption[] = [
    {
      id: 'profit-loss',
      label: 'Profit Loss',
      onClick: () => console.log('Navigating to Profit Loss'),
      href: '/analytics/profit-loss',
    },
    {
      id: 'profit-by-sku',  
      label: 'Profit by SKU',
      onClick: () => console.log('Navigating to Profit by SKU'),
      href: '/analytics/profit-by-sku'
    },
    {
      id: 'profit-by-asin',
      label: 'Profit by ASIN',
      onClick: () => console.log('Navigating to Profit by ASIN'),
      href: '/analytics/profit-by-asin'
    },
    {
      id: 'profit-by-category',
      label: 'Profit by Category',
      onClick: () => console.log('Navigating to Profit by Category'),
      href: '/analytics/profit-by-category'
    },
  ]
  
  return (
    <div className={styles.container}>
      <div 
        className={`${styles.sidebar} ${isExpanded ? styles.expanded : styles.collapsed}`}
      >
        <div className={styles.logo}>
          {isExpanded ? (
            <div className={styles.expandedLogoContainer}>
                <svg width="107" height="16" viewBox="0 0 107 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_1039_1375)">
                    <path fillRule="evenodd" clipRule="evenodd" d="M0.119701 3.61867C0.053592 3.61867 0 3.56508 0 3.49897V0.14735C0 0.0812409 0.053592 0.0276489 0.119701 0.0276489H15.0623C15.5113 0.0276489 15.9461 0.0881808 16.3591 0.201528C18.0162 0.656352 19.3214 1.96157 19.7762 3.61867C19.8896 4.03166 19.9501 4.46646 19.9501 4.91541C19.9501 5.36441 19.8896 5.7992 19.7762 6.21217C19.5934 6.87835 19.2731 7.48766 18.8478 8.00768C19.2731 8.52774 19.5934 9.13706 19.7762 9.80319C19.8896 10.2162 19.9501 10.651 19.9501 11.1C19.9501 11.5489 19.8896 11.9837 19.7762 12.3967C19.3214 14.0538 18.0162 15.3591 16.3591 15.8139C15.9461 15.9272 15.5113 15.9877 15.0623 15.9877H0.119701C0.053592 15.9877 0 15.9341 0 15.868V6.33187C0 6.2658 0.053592 6.21217 0.119701 6.21217H16.3591V3.61867H0.119701ZM16.3591 12.3967V9.80319H3.59102V12.3967H16.3591Z" fill="var(--static-text-strong)"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M37.9052 15.8139C39.5623 15.3591 40.8674 14.0538 41.3223 12.3967C41.4356 11.9837 41.4963 11.5489 41.4963 11.1V4.91541C41.4963 4.46646 41.4356 4.03166 41.3223 3.61867C40.8674 1.96157 39.5623 0.656352 37.9052 0.201529C37.4923 0.0881812 37.0574 0.0276489 36.6085 0.0276489H26.4339C25.985 0.0276489 25.5501 0.0881812 25.1372 0.201529C23.4801 0.656352 22.1748 1.96157 21.72 3.61867C21.6067 4.03166 21.5461 4.46646 21.5461 4.91541V11.1C21.5461 11.5489 21.6067 11.9837 21.72 12.3967C22.1748 14.0538 23.4801 15.3591 25.1372 15.8139C25.5501 15.9272 25.985 15.9877 26.4339 15.9877H36.6085C37.0574 15.9877 37.4923 15.9272 37.9052 15.8139ZM37.9052 12.3967V3.61867L25.1372 3.61867V12.3967H37.9052Z" fill="var(--static-text-strong)"/>
                    <path d="M43.212 3.61867C43.1458 3.61867 43.0923 3.56508 43.0923 3.49897V0.147352C43.0923 0.0812425 43.1458 0.0276507 43.212 0.0276507L46.4359 0.0276504C47.8807 0.0276502 49.2517 0.66685 50.1802 1.77363L53.0673 5.21435L55.9545 1.77362C56.883 0.66685 58.254 0.0276492 59.6988 0.0276491L62.9227 0.0276489C62.9889 0.0276489 63.0424 0.0812409 63.0424 0.14735V3.49897C63.0424 3.56508 62.9889 3.61867 62.9227 3.61867H59.0939L55.4111 8.00764L59.0943 12.3967H62.9227C62.9889 12.3967 63.0424 12.4503 63.0424 12.5164V15.868C63.0424 15.9341 62.9889 15.9877 62.9227 15.9877H59.6988C58.254 15.9877 56.8834 15.3485 55.9545 14.2418L53.0673 10.801L50.1802 14.2418C49.2513 15.3485 47.8807 15.9877 46.4359 15.9877H43.212C43.1458 15.9877 43.0923 15.9341 43.0923 15.868V12.5164C43.0923 12.4503 43.1458 12.3967 43.212 12.3967H47.0404L50.7236 8.00764L47.0408 3.61867H43.212Z" fill="var(--static-text-strong)"/>
                    <path d="M84.4689 0.0276489C84.5351 0.0276489 84.5886 0.0812409 84.5886 0.14735V3.49897C84.5886 3.56508 84.5351 3.61867 84.4689 3.61867H68.2295V6.21217H84.4689C84.5351 6.21217 84.5886 6.2658 84.5886 6.33187V9.68349C84.5886 9.74961 84.5351 9.80319 84.4689 9.80319H68.2295V12.3967H84.4689C84.5351 12.3967 84.5886 12.4503 84.5886 12.5164V15.868C84.5886 15.9341 84.5351 15.9877 84.4689 15.9877H69.5262C69.0773 15.9877 68.6424 15.9272 68.2295 15.8139C66.5724 15.3591 65.2673 14.0538 64.8124 12.3967C64.6991 11.9837 64.6384 11.5489 64.6384 11.1V4.91541C64.6384 4.46646 64.6991 4.03166 64.8124 3.61867C65.2673 1.96157 66.5724 0.656352 68.2295 0.201529C68.6424 0.0881812 69.0773 0.0276489 69.5262 0.0276489H84.4689Z" fill="var(--static-text-strong)"/>
                    <path d="M86.3043 15.9877C86.238 15.9877 86.1846 15.9341 86.1846 15.868V0.14735C86.1846 0.0812405 86.238 0.0276489 86.3043 0.0276489H101.247C101.696 0.0276489 102.131 0.0881812 102.544 0.201529C104.201 0.656352 105.506 1.96157 105.961 3.61867C106.074 4.03166 106.135 4.46646 106.135 4.91541V15.868C106.135 15.9341 106.081 15.9877 106.015 15.9877H102.663C102.597 15.9877 102.544 15.9341 102.544 15.868V3.61867H97.9551V15.868C97.9551 15.9341 97.9017 15.9877 97.8354 15.9877H94.4838C94.4176 15.9877 94.3641 15.9341 94.3641 15.868V3.61867H89.7756V15.868C89.7756 15.9341 89.7221 15.9877 89.6559 15.9877H86.3043Z" fill="var(--static-text-strong)"/>
                    </g>
                    <defs>
                    <clipPath id="clip0_1039_1375">
                    <rect width="106.135" height="16" fill="white"/>
                    </clipPath>
                    </defs>
                </svg>
            </div>
          ) : (
            <IconBoxemB/>
          )}
        </div>
        
        {/* Dashboard NavItem */}
            <div className={`${isExpanded ? styles.expandedNavItemsContainer : styles.collapsedNavItemsContainer}`}>
                <NavItem
                    logo={
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.5556 9.33333V3H21V9.33333H12.5556ZM2 13.5556V3H10.4444V13.5556H2ZM12.5556 22V11.4444H21V22H12.5556ZM2 22V15.6667H10.4444V22H2ZM4.11111 11.4444H8.33333V5.11111H4.11111V11.4444ZM14.6667 19.8889H18.8889V13.5556H14.6667V19.8889ZM14.6667 7.22222H18.8889V5.11111H14.6667V7.22222ZM4.11111 19.8889H8.33333V17.7778H4.11111V19.8889Z" fill="currentColor"/>
                        </svg>

                    }
                    label="Dashboard"
                    currentLocation={location}
                    isOpen={false}
                    onClick={(href, e) => handleNavItemClick('dashboard', href, e)}
                    onToggleDropdown={() => {}}
                    onOptionClick={handleOptionClick}
                    isCollapsed={!isExpanded}
                    href={'/'}
                    isMainTabActive={location === '/'}
                />
                
                {/* Inventory NavItem with dropdown */}
                <NavItem
                    logo={
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11.4444 20.2962V13.1037L5.11111 9.455V16.6475L11.4444 20.2962ZM13.5556 20.2962L19.8889 16.6475V9.455L13.5556 13.1037V20.2962ZM11.4444 22.7113L4.05556 18.485C3.7213 18.2925 3.46181 18.0387 3.27708 17.7237C3.09236 17.4087 3 17.0587 3 16.6737V8.32625C3 7.94125 3.09236 7.59125 3.27708 7.27625C3.46181 6.96125 3.7213 6.7075 4.05556 6.515L11.4444 2.28875C11.7787 2.09625 12.1306 2 12.5 2C12.8694 2 13.2213 2.09625 13.5556 2.28875L20.9444 6.515C21.2787 6.7075 21.5382 6.96125 21.7229 7.27625C21.9076 7.59125 22 7.94125 22 8.32625V16.6737C22 17.0587 21.9076 17.4087 21.7229 17.7237C21.5382 18.0387 21.2787 18.2925 20.9444 18.485L13.5556 22.7113C13.2213 22.9037 12.8694 23 12.5 23C12.1306 23 11.7787 22.9037 11.4444 22.7113ZM16.7222 8.85125L18.7542 7.69625L12.5 4.1L10.4417 5.28125L16.7222 8.85125ZM12.5 11.2925L14.5583 10.1112L8.30417 6.515L6.24583 7.69625L12.5 11.2925Z" fill="currentColor"/>
                        </svg>

                    }
                    label="Inventory"
                    dropdownOptions={inventoryOptions}
                    currentLocation={location}
                    isOpen={openDropdowns['inventory'] || false}
                    onClick={(href, e) => handleNavItemClick('inventory', href, e)}
                    onToggleDropdown={() => {}}
                    onOptionClick={handleOptionClick}
                    isCollapsed={!isExpanded}
                    href={'/inventory'}
                    isMainTabActive={location === '/inventory'}
                />

                <NavItem
                    logo={
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 20C5.16667 20 4.45833 19.7083 3.875 19.125C3.29167 18.5417 3 17.8333 3 17H1V6C1 5.45 1.19583 4.97917 1.5875 4.5875C1.97917 4.19583 2.45 4 3 4H17V8H20L23 12V17H21C21 17.8333 20.7083 18.5417 20.125 19.125C19.5417 19.7083 18.8333 20 18 20C17.1667 20 16.4583 19.7083 15.875 19.125C15.2917 18.5417 15 17.8333 15 17H9C9 17.8333 8.70833 18.5417 8.125 19.125C7.54167 19.7083 6.83333 20 6 20ZM6 18C6.28333 18 6.52083 17.9042 6.7125 17.7125C6.90417 17.5208 7 17.2833 7 17C7 16.7167 6.90417 16.4792 6.7125 16.2875C6.52083 16.0958 6.28333 16 6 16C5.71667 16 5.47917 16.0958 5.2875 16.2875C5.09583 16.4792 5 16.7167 5 17C5 17.2833 5.09583 17.5208 5.2875 17.7125C5.47917 17.9042 5.71667 18 6 18ZM3 15H3.8C4.08333 14.7 4.40833 14.4583 4.775 14.275C5.14167 14.0917 5.55 14 6 14C6.45 14 6.85833 14.0917 7.225 14.275C7.59167 14.4583 7.91667 14.7 8.2 15H15V6H3V15ZM18 18C18.2833 18 18.5208 17.9042 18.7125 17.7125C18.9042 17.5208 19 17.2833 19 17C19 16.7167 18.9042 16.4792 18.7125 16.2875C18.5208 16.0958 18.2833 16 18 16C17.7167 16 17.4792 16.0958 17.2875 16.2875C17.0958 16.4792 17 16.7167 17 17C17 17.2833 17.0958 17.5208 17.2875 17.7125C17.4792 17.9042 17.7167 18 18 18ZM17 13H21.25L19 10H17V13Z" fill="currentColor"/>
                        </svg>
                    }
                    label="Shipments"
                    dropdownOptions={shipmentsOptions}
                    currentLocation={location}
                    isOpen={openDropdowns['shipments'] || false}
                    onClick={(href, e) => handleNavItemClick('shipments', href, e)}
                    onToggleDropdown={() => {}}
                    onOptionClick={handleOptionClick}
                    isCollapsed={!isExpanded}
                    href={'/shipments'}
                    isMainTabActive={location === '/shipments'}
                />

                <NavItem
                    logo={
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18.4 8.8H18.1714C17.2914 8.8 16.5714 9.52 16.5714 10.4V18.4C16.5714 19.28 17.2914 20 18.1714 20H18.4C19.28 20 20 19.28 20 18.4V10.4C20 9.52 19.28 8.8 18.4 8.8ZM12 4C11.12 4 10.4 4.72 10.4 5.6V18.4C10.4 19.28 11.12 20 12 20C12.88 20 13.6 19.28 13.6 18.4V5.6C13.6 4.72 12.88 4 12 4ZM5.6 13.1429C4.72 13.1429 4 13.8629 4 14.7429V18.4C4 19.28 4.72 20 5.6 20C6.48 20 7.2 19.28 7.2 18.4V14.7429C7.2 13.8629 6.48 13.1429 5.6 13.1429Z" fill="currentColor"/>
                        </svg>

                    }
                    label="Analytics"
                    currentLocation={location}
                    isOpen={false}
                    onClick={(href, e) => handleNavItemClick('analytics', href, e)}
                    onToggleDropdown={() => {}}
                    onOptionClick={handleOptionClick}
                    isCollapsed={!isExpanded}
                    href={'/analytics/profit-by-sku?analytics=true'}
                    isMainTabActive={location.startsWith('/analytics')}
                />

                <NavItem
                    logo={
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9.24995 22L8.84995 18.8C8.63328 18.7167 8.42912 18.6167 8.23745 18.5C8.04578 18.3833 7.85828 18.2583 7.67495 18.125L4.69995 19.375L1.94995 14.625L4.52495 12.675C4.50828 12.5583 4.49995 12.4458 4.49995 12.3375V11.6625C4.49995 11.5542 4.50828 11.4417 4.52495 11.325L1.94995 9.375L4.69995 4.625L7.67495 5.875C7.85828 5.74167 8.04995 5.61667 8.24995 5.5C8.44995 5.38333 8.64995 5.28333 8.84995 5.2L9.24995 2H14.75L15.15 5.2C15.3666 5.28333 15.5708 5.38333 15.7625 5.5C15.9541 5.61667 16.1416 5.74167 16.325 5.875L19.3 4.625L22.05 9.375L19.475 11.325C19.4916 11.4417 19.5 11.5542 19.5 11.6625V12.3375C19.5 12.4458 19.4833 12.5583 19.45 12.675L22.025 14.625L19.275 19.375L16.325 18.125C16.1416 18.2583 15.95 18.3833 15.75 18.5C15.55 18.6167 15.35 18.7167 15.15 18.8L14.75 22H9.24995ZM11 20H12.975L13.325 17.35C13.8416 17.2167 14.3208 17.0208 14.7625 16.7625C15.2041 16.5042 15.6083 16.1917 15.975 15.825L18.45 16.85L19.425 15.15L17.275 13.525C17.3583 13.2917 17.4166 13.0458 17.45 12.7875C17.4833 12.5292 17.5 12.2667 17.5 12C17.5 11.7333 17.4833 11.4708 17.45 11.2125C17.4166 10.9542 17.3583 10.7083 17.275 10.475L19.425 8.85L18.45 7.15L15.975 8.2C15.6083 7.81667 15.2041 7.49583 14.7625 7.2375C14.3208 6.97917 13.8416 6.78333 13.325 6.65L13 4H11.025L10.675 6.65C10.1583 6.78333 9.67912 6.97917 9.23745 7.2375C8.79578 7.49583 8.39162 7.80833 8.02495 8.175L5.54995 7.15L4.57495 8.85L6.72495 10.45C6.64162 10.7 6.58328 10.95 6.54995 11.2C6.51662 11.45 6.49995 11.7167 6.49995 12C6.49995 12.2667 6.51662 12.525 6.54995 12.775C6.58328 13.025 6.64162 13.275 6.72495 13.525L4.57495 15.15L5.54995 16.85L8.02495 15.8C8.39162 16.1833 8.79578 16.5042 9.23745 16.7625C9.67912 17.0208 10.1583 17.2167 10.675 17.35L11 20ZM12.05 15.5C13.0166 15.5 13.8416 15.1583 14.525 14.475C15.2083 13.7917 15.55 12.9667 15.55 12C15.55 11.0333 15.2083 10.2083 14.525 9.525C13.8416 8.84167 13.0166 8.5 12.05 8.5C11.0666 8.5 10.2375 8.84167 9.56245 9.525C8.88745 10.2083 8.54995 11.0333 8.54995 12C8.54995 12.9667 8.88745 13.7917 9.56245 14.475C10.2375 15.1583 11.0666 15.5 12.05 15.5Z" fill="currentColor"/>
                        </svg>

                    }
                    label="Settings"
                    currentLocation={location}
                    isOpen={false}
                    onClick={(href, e) => handleNavItemClick('settings', href, e)}
                    onToggleDropdown={() => {}}
                    onOptionClick={handleOptionClick}
                    isCollapsed={!isExpanded}
                    href={'/settings/details'}
                    isMainTabActive={location.startsWith('/settings')}
                />
                <NavItem
                    logo={
                      <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6.5 14.9601C5.95 14.9601 5.47917 14.7642 5.0875 14.3726C4.69583 13.9809 4.5 13.5101 4.5 12.9601C4.5 12.4101 4.69583 11.9392 5.0875 11.5476C5.47917 11.1559 5.95 10.9601 6.5 10.9601C7.05 10.9601 7.52083 11.1559 7.9125 11.5476C8.30417 11.9392 8.5 12.4101 8.5 12.9601C8.5 13.5101 8.30417 13.9809 7.9125 14.3726C7.52083 14.7642 7.05 14.9601 6.5 14.9601ZM12.5 14.9601C11.95 14.9601 11.4792 14.7642 11.0875 14.3726C10.6958 13.9809 10.5 13.5101 10.5 12.9601C10.5 12.4101 10.6958 11.9392 11.0875 11.5476C11.4792 11.1559 11.95 10.9601 12.5 10.9601C13.05 10.9601 13.5208 11.1559 13.9125 11.5476C14.3042 11.9392 14.5 12.4101 14.5 12.9601C14.5 13.5101 14.3042 13.9809 13.9125 14.3726C13.5208 14.7642 13.05 14.9601 12.5 14.9601ZM18.5 14.9601C17.95 14.9601 17.4792 14.7642 17.0875 14.3726C16.6958 13.9809 16.5 13.5101 16.5 12.9601C16.5 12.4101 16.6958 11.9392 17.0875 11.5476C17.4792 11.1559 17.95 10.9601 18.5 10.9601C19.05 10.9601 19.5208 11.1559 19.9125 11.5476C20.3042 11.9392 20.5 12.4101 20.5 12.9601C20.5 13.5101 20.3042 13.9809 19.9125 14.3726C19.5208 14.7642 19.05 14.9601 18.5 14.9601Z" fill="currentColor"/>
                      </svg>
                    }
                    label="More"
                    currentLocation={location}
                    isOpen={false}
                    onClick={(href, e) => handleNavItemClick('more', href, e)}
                    onToggleDropdown={() => {}}
                    onOptionClick={handleOptionClick}
                    isCollapsed={!isExpanded}
                    href={'/more'}
                    isMainTabActive={location === '/more'}
                />

            </div>
            <div className={styles.accountContainer}>
                  <NavItem
                      logo={
                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6.35 17.1C7.2 16.45 8.15 15.9375 9.2 15.5625C10.25 15.1875 11.35 15 12.5 15C13.65 15 14.75 15.1875 15.8 15.5625C16.85 15.9375 17.8 16.45 18.65 17.1C19.2333 16.4167 19.6875 15.6417 20.0125 14.775C20.3375 13.9083 20.5 12.9833 20.5 12C20.5 9.78333 19.7208 7.89583 18.1625 6.3375C16.6042 4.77917 14.7167 4 12.5 4C10.2833 4 8.39583 4.77917 6.8375 6.3375C5.27917 7.89583 4.5 9.78333 4.5 12C4.5 12.9833 4.6625 13.9083 4.9875 14.775C5.3125 15.6417 5.76667 16.4167 6.35 17.1ZM12.5 13C11.5167 13 10.6875 12.6625 10.0125 11.9875C9.3375 11.3125 9 10.4833 9 9.5C9 8.51667 9.3375 7.6875 10.0125 7.0125C10.6875 6.3375 11.5167 6 12.5 6C13.4833 6 14.3125 6.3375 14.9875 7.0125C15.6625 7.6875 16 8.51667 16 9.5C16 10.4833 15.6625 11.3125 14.9875 11.9875C14.3125 12.6625 13.4833 13 12.5 13ZM12.5 22C11.1167 22 9.81667 21.7375 8.6 21.2125C7.38333 20.6875 6.325 19.975 5.425 19.075C4.525 18.175 3.8125 17.1167 3.2875 15.9C2.7625 14.6833 2.5 13.3833 2.5 12C2.5 10.6167 2.7625 9.31667 3.2875 8.1C3.8125 6.88333 4.525 5.825 5.425 4.925C6.325 4.025 7.38333 3.3125 8.6 2.7875C9.81667 2.2625 11.1167 2 12.5 2C13.8833 2 15.1833 2.2625 16.4 2.7875C17.6167 3.3125 18.675 4.025 19.575 4.925C20.475 5.825 21.1875 6.88333 21.7125 8.1C22.2375 9.31667 22.5 10.6167 22.5 12C22.5 13.3833 22.2375 14.6833 21.7125 15.9C21.1875 17.1167 20.475 18.175 19.575 19.075C18.675 19.975 17.6167 20.6875 16.4 21.2125C15.1833 21.7375 13.8833 22 12.5 22ZM12.5 20C13.3833 20 14.2167 19.8708 15 19.6125C15.7833 19.3542 16.5 18.9833 17.15 18.5C16.5 18.0167 15.7833 17.6458 15 17.3875C14.2167 17.1292 13.3833 17 12.5 17C11.6167 17 10.7833 17.1292 10 17.3875C9.21667 17.6458 8.5 18.0167 7.85 18.5C8.5 18.9833 9.21667 19.3542 10 19.6125C10.7833 19.8708 11.6167 20 12.5 20ZM12.5 11C12.9333 11 13.2917 10.8583 13.575 10.575C13.8583 10.2917 14 9.93333 14 9.5C14 9.06667 13.8583 8.70833 13.575 8.425C13.2917 8.14167 12.9333 8 12.5 8C12.0667 8 11.7083 8.14167 11.425 8.425C11.1417 8.70833 11 9.06667 11 9.5C11 9.93333 11.1417 10.2917 11.425 10.575C11.7083 10.8583 12.0667 11 12.5 11Z" fill="currentColor"/>
                        </svg>
                      }
                      label="Account"
                      currentLocation={location}
                      isOpen={false}
                      onClick={(href, e) => handleNavItemClick('account', href, e)}
                      onToggleDropdown={() => {}}
                      onOptionClick={handleOptionClick}
                      isCollapsed={!isExpanded}
                      href={'/account/profile'}
                      isMainTabActive={location.startsWith('/account')}
                  />
            </div>
        </div>
        
        <button 
            onClick={toggleSidebar}
            className={`${styles.toggleButton} ${isExpanded ? styles.rotated : ''}`}
        >
            <svg width="9" height="14" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.64996 7L0.666626 2.01667L2.18329 0.5L8.68329 7L2.18329 13.5L0.666626 11.9833L5.64996 7Z" fill="var(--static-text-inverse-strong)"/>
            </svg>
        </button>
    </div>
  );
};

export default Sidebar;